import {ReactChildren, ReactElement} from 'react'
import {FormErrorType} from './types'
import find from 'lodash/find'
import pull from 'lodash/pull'
import filter from 'lodash/filter'
import findIndex from 'lodash/findIndex'
import map from 'lodash/map'
import includes from 'lodash/includes'
import isNaN from 'lodash/isNaN'

export const isChildFormStep = (child: any) => {
    return (typeof child.type !== 'string' && child.type.displayName === 'PantaFormStep')
}

export const countNrOfSteps = (children: ReactChildren, props: any) => {
    let nrOfSteps = 0
    children.map(props.children, (child: ReactElement) => {
        if (isChildFormStep(child)) {
            nrOfSteps += 1
        }
    })
    return nrOfSteps
}

export const appendError = (
    errors: FormErrorType[],
    fieldKey: string,
    error: string
) => {
    const newErrors = [...errors]
    const field = find(newErrors, { key: fieldKey })
    if (field) {
        // If errors already exists for the field
        const errorExists = find(field.messages, error)
        if (!errorExists) {
            // Add error to field, if it does not already exist
            field.messages = [...field.messages, error]
        }
    } else {
        // If no errors exist for the field, add field with error
        newErrors.push({ key: fieldKey, messages: [error] })
    }
    return newErrors
}

export const removeError = (
    errors: FormErrorType[],
    fieldKey: string,
    error: string
) => {
    let newErrors = [...errors]
    const field = find(newErrors, { key: fieldKey })
    if (field) {
        pull(field.messages, error)
        if (field.messages && field.messages.length === 0) {
            newErrors = filter(newErrors, err => err.key !== fieldKey)
        }
    }
    return newErrors
}

export const replaceError = (
    errors: FormErrorType[],
    fieldKey: string,
    newError: string
) => {
    const newErrors = [...errors]
    const fieldIndex = findIndex(newErrors, { key: fieldKey })
    newErrors.splice(fieldIndex, 1, { key: fieldKey, messages: [newError] })
    return newErrors
}

export const removeAllErrorsForKey = (
    errors: FormErrorType[],
    fieldKey: string
) => {
    let newErrors = [...errors]
    newErrors = filter(newErrors, err => err.key !== fieldKey)
    return newErrors
}

export const getAllErrors = (errors: FormErrorType[]) => {
    const allErrors: string[] = []
    map(errors, err => {
        map(err.messages, msg => {
            if (!includes(allErrors, msg)) {
                allErrors.push(msg)
            }
        })
    })
    return allErrors
}

export const validateRequired = (
    value: any,
    key: string,
    message: string,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]

    const isEmpty =
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '');

    if (isEmpty) {
        errors = appendError(errors, key, message)
    } else {
        errors = removeError(errors, key, message)
    }
    return errors
}

export const validateRequiredArray = (
    value: any,
    key: string,
    message: string,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]

    if (!value || !value.length) {
        errors = appendError(errors, key, message)
    } else {
        errors = removeError(errors, key, message)
    }
    return errors
}

export const validateIsNumber = (
    value: any,
    key: string,
    message: string,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]
    if (isNaN(new Number(value))) {
        errors = appendError(errors, key, message)
    } else {
        errors = removeError(errors, key, message)
    }
    return errors
}

export const validateIsEmail = (
    value: string,
    key: string,
    message: string,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]

    if (value === '') return errors

    if (
        // eslint-disable-next-line no-useless-escape
        !/^[\w_\+-]+(\.[\w_\+-]+)*@[a-z0-9-]+(\.[a-z0-9-]+)*\.([a-z]{2,63})$/i.test(
            value
        )
    ) {
        errors = appendError(errors, key, message)
    } else {
        errors = removeError(errors, key, message)
    }
    return errors
}

export const validateIsZipCode = (
    value: string,
    key: string,
    message: string,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]

    const regex = new RegExp(/^\d{3} ?\d{2}$/)
    const test = regex.test(value.trim())

    if (!test) {
        errors = appendError(errors, key, message)
    } else {
        errors = removeError(errors, key, message)
    }

    return errors
}

export const validateIsBankgiro = (
    value: string,
    key: string,
    message: string,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]

    const regex = new RegExp(/\d{3,4}-\d{4}$/, 'gi')
    const test = regex.test(value.trim())

    if (!test || !validateLuhn(value.trim())) {
        errors = appendError(errors, key, message)
    } else {
        errors = removeError(errors, key, message)
    }

    return errors
}

export const validateIsPlusgiro = (
    value: string,
    key: string,
    message: string,
    currentErrors: FormErrorType[]
) => {
    let errors = [...currentErrors]

    const regex = new RegExp(/\d{1,7}-\d{1}$/, 'gi')
    const test = regex.test(value.trim())

    if (!test || !validateLuhn(value.trim())) {
        errors = appendError(errors, key, message)
    } else {
        errors = removeError(errors, key, message)
    }

    return errors
}

function validateLuhn(value: string): boolean {
    // accept only digits, dashes or spaces
    if (/[^0-9-\s]+/.test(value)) return false

    // The Luhn Algorithm.
    let nCheck = 0,
        nDigit = 0,
        bEven = false
    value = value.replace(/\D/g, '')

    for (let n = value.length - 1; n >= 0; n--) {
        const cDigit = value.charAt(n)
        nDigit = parseInt(cDigit, 10)

        if (bEven) {
            if ((nDigit *= 2) > 9) nDigit -= 9
        }

        nCheck += nDigit
        bEven = !bEven
    }

    return nCheck % 10 === 0
}
